import { Device } from "../../../features/devices/deviceSlice";
import {
  Email as EmailType,
  Status as StatusType,
  Configuration as ConfigurationType,
  Operators,
  Rule,
  Hysteresis,
  HysteresisMode,
  Rules as RulesType,
} from "../../../features/notifications/notificationSlice";
import { useGetGroupUserInfoQuery } from "../../../services/userAPI";
import { useAppSelector } from "../../../app/hooks";
import { selectDeviceByEUI } from "../../../features/devices/deviceSlice";

export const GetGroupUserInfo = ({
  groupid,
  userid,
}: {
  groupid: string;
  userid: string;
}) => {
  const { data, isLoading, error } = useGetGroupUserInfoQuery({
    groupID: groupid || "",
    userID: userid,
  });
  return { data, isLoading, error };
};

export const mapObjectToArray = (mapObject: any) => {
  Object.keys(mapObject).map((key) => {
    return { id: key, ...mapObject[key] };
  });
};

export const generateBsonObjectId = () => {
  // Timestamp (4 bytes)
  const timestamp = Math.floor(new Date().getTime() / 1000).toString(16);

  // Random value (5 bytes)
  const randomValue = Math.floor(Math.random() * 0xffffff)
    .toString(16)
    .padStart(5, "0");

  // Machine identifier and process identifier (3 bytes + 2 bytes)
  const machineIdentifier = Math.floor(Math.random() * 0xffffff)
    .toString(16)
    .padStart(6, "0");
  const processIdentifier = Math.floor(Math.random() * 0xffff)
    .toString(16)
    .padStart(4, "0");

  // Counter (3 bytes)
  let counter = Math.floor(Math.random() * 0xffffff)
    .toString(16)
    .padStart(6, "0");

  return timestamp + machineIdentifier + processIdentifier + randomValue + counter;
};

export const convertDaysAndHoursToMinutes = (days: number, hours: number) => {
  const minutesInADay = 1440;
  const minutesInAnHour = 60;
  return days * minutesInADay + hours * minutesInAnHour;
};

export const convertMinutesToDaysAndHours = (min: number) => {
  const minutesInADay = 1440;
  const minutesInAnHour = 60;
  // Calculate days and remaining hours
  const days = Math.floor(min / minutesInADay);
  const hours = Math.floor((min % minutesInADay) / minutesInAnHour);

  return {
    days: days,
    hours: hours,
  };
};

export const MagDevicePresets: { id: string; label: string }[] = [
  { id: "1", label: "Notify me when switch is closed" },
  { id: "2", label: "Notify me when switch is open" },
  { id: "3", label: "Custom" },
];

export const extractDeviceParameters = (device: Device | null) => {
  if (!device || !device.configuration) return null;

  const result: any[] = [];

  for (const configurationKey in device.configuration) {
    const paramArray = device.configuration[configurationKey];

    if (Array.isArray(paramArray)) {
      paramArray.forEach((item: any) => {
        result.push({
          id: `${configurationKey}.${item.elemID}`,
          parameter: configurationKey,
          description: item.description,
          elemID: item.elemID,
        });
      });
    }
  }
  return result;
};

// export const isMagDevice = (deviceEUI: string): boolean => {
//   const device = useAppSelector((state) => selectDeviceByEUI(state, deviceEUI));
//   if (!device?.configuration) return false;

//   for (const key in device.configuration) {
//     const paramArray = device.configuration[key];
//     if (Array.isArray(paramArray)) {
//       if (
//         paramArray.some(
//           (item: any) =>
//             item.elemID?.startsWith("sw_curr") || item.elemID?.startsWith("sw_count")
//         )
//       ) {
//         return true;
//       }
//     }
//   }
//   return false;
// };

export const useIsMagDevice = (deviceEUI: string): boolean => {
  const device = useAppSelector((state) => selectDeviceByEUI(state, deviceEUI));

  if (!device?.configuration) return false;

  for (const key in device.configuration) {
    const paramArray = device.configuration[key];
    if (Array.isArray(paramArray)) {
      if (
        paramArray.some(
          (item: any) =>
            item.elemID?.startsWith("sw_curr") || item.elemID?.startsWith("sw_count")
        )
      ) {
        return true;
      }
    }
  }

  return false;
};

export const defaultStatus: StatusType = {
  active: true,
  current: false,
  triggered_at: undefined,
  last_trigger: undefined,
  evaluated_at: undefined,
  acknowledged_at: undefined,
};

export const defaultConfiguration: ConfigurationType = {
  repeat_interval: 0,
  hold_on: 0,
  hold_off: 0,
  assert_on: false,
  latch_on: false,
};

export const defaultEmail: EmailType = { subject: "", body: "", trigger: new Date() };

export type ErrorType = {
  name: boolean;
  users: boolean;
  email: {
    subject: boolean;
    body: boolean;
  };
  rules: Record<string, boolean>;
};

// export const convertArrayToMapObject = (
//   // array: Array<{ [key: string]: any }>
//   array: Array<Rule>
// ): Record<string, Omit<Rule, "id">> => {
//   const transformedRules: Record<string, Omit<Rule, "id">> = {};

//   array.forEach(({ id, ...rest }) => {
//     if (!id) {
//       // Optionally log warning or skip silently
//       return;
//     }
//     transformedRules[id] = rest;
//   });

//   return transformedRules;
// };

export const convertRulesArrayToMapObject = (
  array: Array<Rule>
): Record<string, Omit<Rule, "id">> => {
  const transformedRules: Record<string, Omit<Rule, "id">> = {};

  array.forEach(({ id, ...rest }) => {
    if (!id) return;
    transformedRules[id] = rest;
  });
  return transformedRules;
};

export const convertMapObjectToArray = (
  mapObject: Record<string, any> | undefined | null = null
): Array<any> | null => {
  if (typeof mapObject !== "object" || mapObject === null) return null;

  const keys = Object.keys(mapObject);
  if (keys.length === 0) return null;

  return keys.map((id) => ({
    id,
    ...mapObject[id],
  }));
};

export const getDefaultRule = (): Rule => ({
  id: generateBsonObjectId(),
  spec: {
    device_eui: "",
    parameter: "",
    operator: Operators.DF,
    threshold: 0,
    hysteresis: {
      value: 0,
      mode: HysteresisMode.NONE,
    },
  },
});

export const ruleSpecKeys = {
  id: "id",
  notificationID: "notificationID",
  device_eui: "device_eui",
  parameter: "parameter",
  operator: "operator",
  threshold: "threshold",
  hysteresisValue: "hysteresis.value",
  hysteresisMode: "hysteresis.mode",
} as const;

export const switchOperators = [Operators.OPEN, Operators.CLOSED, Operators.EITHER];
export const ruleSpecRequiredFields: string[] = [
  ruleSpecKeys.device_eui,
  ruleSpecKeys.parameter,
  ruleSpecKeys.operator,
];

export const isValidateRule = (rule: Rule): boolean => {
  const { id, spec } = rule;
  if (!id) return false;
  if (!spec) return false;

  for (const field of ruleSpecRequiredFields) {
    const value: string | number = spec[field as keyof Rule["spec"]];
    if (value === undefined || (typeof value === "string" && value === "")) {
      return false;
    }
  }

  if (
    spec.operator === Operators.DF ||
    !Object.values(Operators).includes(spec.operator!)
  ) {
    return false;
  }

  const isValidHysteresis = (h: Hysteresis | undefined): boolean => {
    // NONE => 0
    // ABSOLUTE => Integer
    // % => Positive number

    if (!h) return false;
    const { mode, value } = h;
    // Ensure mode is valid enum member
    if (!Object.values(HysteresisMode).includes(mode)) {
      return false;
    }

    switch (mode) {
      case HysteresisMode.NONE:
        return value === 0;

      case HysteresisMode.ABSOLUTE:
        // Integer
        return Number.isInteger(value);

      case HysteresisMode.PERCENTAGE:
        // Only positive numbers allowed
        return value > 0;

      default:
        return false;
    }
  };

  // If not switch operator, threshold and hysteresis required
  if (!switchOperators.includes(spec.operator!)) {
    if (
      spec.threshold === undefined ||
      spec.threshold === "" ||
      Number.isNaN(spec.threshold)
    ) {
      return false;
    }
    if (!isValidHysteresis(spec?.hysteresis)) return false;
  }

  return true;
};
